import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";
import { AiOutlineLoading3Quarters } from "react-icons/ai"; // Loading icon

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(""); // To show success message
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    // Basic email format check (optional but recommended)
    if (!/\S+@\S+\.\S+/.test(email)) {
       toast.error("Please enter a valid email address.");
       return;
    }

    setLoading(true);
    setMessage(""); // Clear previous messages
    try {
      const success = await forgotPassword(email);
      if (success) {
          setMessage(
            "Password reset email sent successfully. Please check your inbox (and spam folder)."
          );
          setEmail(""); // Clear email field on success
      }
      // Error toast is handled within the store function if !success
    } catch (error) {
      // Catch errors thrown by the store function (e.g., network errors)
      // The store already shows a toast for API errors, so we might not need another one here.
      console.error("Forgot password request failed:", error);
      setMessage(""); // Clear message on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(310deg, #555555 4%, white 38%)" }}
    >
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-2xl border border-gray-300">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-6">
          Forgot Password
        </h2>
        <p className="text-center text-gray-600 text-sm mb-6">
          Enter your builder account email address and we'll send you a link to reset your
          password.
        </p>

        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 text-sm rounded-md text-center">
            {message}
          </div>
        )}

        {!message && ( // Only show form if no success message is displayed
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={{ backgroundColor: "#dddddd", borderRadius: "12px" }}
                placeholder="Enter your registered email"
                required
                disabled={loading}
              />
            </div>

            <div className="flex flex-col items-center">
              <button
                type="submit"
                disabled={loading}
                className={`w-full sm:w-auto px-6 py-2 mt-2 text-black border-2 transition-all duration-300 flex items-center justify-center ${
                  loading ? "opacity-70 cursor-not-allowed" : "hover:bg-gray-50" // Add hover effect
                }`}
                style={{
                  background: "linear-gradient(310deg, #dddddd 10%, white)",
                  borderRadius: "24px",
                }}
              >
                {loading ? (
                  <>
                    <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login?type=builder")}
                className="mt-4 text-sm text-blue-600 hover:underline"
                disabled={loading}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}

        {/* Show Back to Login button even after success message */}
        {message && (
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={() => navigate("/login?type=builder")}
              className="text-sm text-blue-600 hover:underline"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;